/*
 * Copyright (c) 2023 Yesan SAS
 * All rights reserved
 *
 * The present software is the confidential and proprietary information of
 * Yesan SAS. You shall not disclose the present software and shall
 * use it only in accordance with the terms of the license agreement you
 * entered into with Yesan SAS. This software may be subject to
 * export or import laws in certain countries.
 */
 /**
 * HUB framework utility functions
 * @class HUBUtils
 * @author Vincent BONESTEVE
 */
export default class HUBUtils {
  ////////////////////////////////////////
  // VALIDATION
  ////////////////////////////////////////
  /**
   * Validate email address using regexp
   * @static
   * @param email email
   * @returns boolean status
   */
  static validateEmail(email){
    return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w\w+)+$/.test(email) === true;
  }

  /**
   * Validate password using regexp
   * @static
   * @param password password
   * @returns boolean status
   */
  static validatePassword(pass){
    return /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,16}$/.test(pass)===true;
  }

  /**
   * Check if a variable is empty or null or void
   * @static
   * @param varToCheck variable
   * @returns boolean status
   */
  static isEmpty(varToCheck){
    if (varToCheck === null){
      return true;
    }
    if (varToCheck === undefined){
      return true;
    }
    if (Array.isArray(varToCheck)){
      return (varToCheck.length === 0);
    }
    if (typeof varToCheck === 'object'){
      if (varToCheck instanceof Date){
        return false;
      }
      return (Object.keys(varToCheck).length === 0);
    }
    if (varToCheck === ""){
      return true;
    }
    return false;
  }


  ////////////////////////////////////////
  // ARRAY
  ////////////////////////////////////////
  /**
   * Check if a value is inside array
   * @static
   * @param array array to search
   * @param val value to search
   * @returns boolean status
   */
  static inArray(array,val){
      if (Array.isArray(array)){
        return (array.indexOf(val) > -1);
      }
      return false;
  }

  /**
   * Join array using delimiter for SQL filter
   * @static
   * @param arr array to join
   * @param delimStr delimiter
   * @returns string
   */
  static joinArrayFilter(arr,delimStr){
    let out = "";
    let countAppended = 0;
    if (Array.isArray(arr)){
      for(let i=0;i<arr.length;i++){
        if (!HUBUtils.isEmpty(arr[i])){
          if (countAppended>0){
            out += delimStr;
          }
          out += arr[i];
          countAppended++;
        }
      }
    }
    return out;
  }

  /**
   * Join array using delimiter and simple quote
   * @static
   * @param arr array to join
   * @param delimStr delimiter
   * @returns string
   */
  static joinArrayFilterSimpleQuote(arr,delimStr){
    let out = "";
    let countAppended = 0;
    if (Array.isArray(arr)){
      for(let i=0;i<arr.length;i++){
        if (!HUBUtils.isEmpty(arr[i])){
          if (countAppended>0){
            out += delimStr;
          }
          out += "'"+arr[i]+"'";
          countAppended++;
        }
      }
    }
    return out;
  }

  /**
   * Join array using delimiter
   * @static
   * @param arr array to join
   * @param delim delimiter
   * @returns string
   */
  static joinArray(arr,delim){
    let out = "";
    let countAppended = 0;
    if (Array.isArray(arr)){
      for(let i=0;i<arr.length;i++){
        if (!HUBUtils.isEmpty(arr[i])){
          if (countAppended>0){
            out += delim;
          }
          out += arr[i];
          countAppended++;
        }
      }
    }
    return out;
  }

  /**
   * Sum array elements having a certain key
   * @static
   * @param arr array to iterate
   * @param keyToSum key for which value will be included in the sum
   * @returns sum
   */
  static arrayKeySum(arr,keyToSum){
    let total = 0;
    for (const item of arr){
      total += item[keyToSum];
    }
    return total;
  }


  ////////////////////////////////////////
  // OBJECT
  ////////////////////////////////////////
  /**
   * Map a function to an object
   * @static
   * @param obj object
   * @param fn function
   */
  static objectMap(obj, fn){
    return Object.fromEntries(
      Object.entries(obj).map(
        ([k, v], i) => [k, fn(v, k, i)]
      )
    );
  }


  ////////////////////////////////////////
  // RANDOM
  ////////////////////////////////////////
  /**
   * Get a random value from array
   * @static
   * @param arr array
   * @returns random value
   */
  static arrayRandom(arr){
    return arr[Math.floor(Math.random()*arr.length)];
  }

  /**
   * Get a random null or value
   * @static
   * @param val value
   * @returns random value
   */
  static randomNullorVal(val){
    if (Math.floor(Math.random()*1000)%2 == 0){
      return "";
    }
    return val;
  }

  /**
   * Get a random date within a range
   * @static
   * @param start start date
   * @param end end date
   * @returns random date
   */
  static randomDate(start, end){
    return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
  }


  ////////////////////////////////////////
  // STRING
  ////////////////////////////////////////
  /**
   * Convert a dropdown object to string
   * @static
   * @param ddval dropdown value
   * @returns string
   */
  static convertDropDownToSring(ddval){
    let val = "";
    if (!HUBUtils.isEmpty(ddval)){
      if (ddval.value == null){
        val = null;
      } else if (ddval.value){
        val = ddval.value;
      } else {
        val = ddval;
      }
    }
    if (!HUBUtils.isEmpty(ddval) && (typeof ddval === "string")){
      val = ddval;
    }
    return val;
  }

  /**
   * Convert a dropdown object to integer
   * @static
   * @param ddval dropdown value
   * @returns integer
   */
  static convertDropDownToInteger(ddval){
    let val = 0;
    if (ddval === null){
      return 0;
    }
    if (Number.isInteger(ddval)){
      val = ddval;
    } else {
      val = ddval.value
    }

    return val;
  }

  /**
   * Truncate string at length
   * @static
   * @param str string to truncate
   * @param len length
   * @returns string
   */
  static truncateStr(str,len){
    if (typeof str === "string"){
      if (str.length <= len) {
        return str;
      }
      return str.slice(0, len) + '...';
    }
    return str;
  }

  /**
   * Capitalize string
   * @static
   * @param str string to capitalize
   * @returns string
   */
  static capitalizeStr(str){
    if (typeof str === "string"){
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
    return str;
  }


  ////////////////////////////////////////
  // MATH
  ////////////////////////////////////////
  /**
   * Linear regression
   * @static
   * @param x_values values on axis x
   * @param y_values values on axis y
   * @returns regressor
   */
  static linearRegression(x_values, y_values){
    //Create the regressor object to store the equation's data
    let regressor = {};

    //Set variables we'll need to get the slope and intercept; we need to find the equation in the format y = m*x+b where m is the slope and b is the intercept
    let x_mean = x_values.reduce((a, b) => a + b, 0)/x_values.length;
    let y_mean = y_values.reduce((a, b) => a + b, 0)/y_values.length;

    //Equations to solve for slope:
    let slope = 0, slope_numerator = 0, slope_denominator = 0;
    let i = 0;
    for(i=0; i<x_values.length; i++){
      slope_numerator += (x_values[i]-x_mean)*(y_values[i]-y_mean);
      slope_denominator += Math.pow((x_values[i]-x_mean),2)
    }

    slope = slope_numerator/slope_denominator;
    regressor['slope'] = slope;

    //Equation to solve for intercept
    let intercept = y_mean - x_mean*slope;
    regressor['intercept'] = intercept;


    //Get y_hat, or predicted values of y based on x_values
    //Loop through x_values, and run the elements through the lr equation to get predictions
    let y_hat = [];
    for(i=0; i<x_values.length; i++){
      y_hat.push(x_values[i]*regressor['slope']+regressor['intercept']);
    }
    regressor['y_hat'] = y_hat;


    //Now to find the r2 score
    let residual_sum_of_squares = 0, total_sum_of_squares = 0, r2 = 0;

    for(i=0; i<y_values.length; i++){
        residual_sum_of_squares+= Math.pow((y_hat[i]-y_values[i]),2);
        total_sum_of_squares += Math.pow((y_hat[i]-y_mean),2);
    }

    r2 = 1- residual_sum_of_squares/total_sum_of_squares;

    //Add to regressor object
    regressor['r2'] = r2;

    return regressor;
  }

  /**
   * Lines intersection
   * @static
   * @param line1_slope line 1 slope
   * @param line1_intercept line 1 intercept
   * @param line2_slope line 2 slope
   * @param line2_intercept line 2 intercept
   * @returns intersection point coordinates
   */
  static findLinesIntersection(line1_slope,line1_intercept, line2_slope,line2_intercept) {
    let x = (line2_intercept - line1_intercept) / (line1_slope - line2_slope);
    let y = line1_slope * x + line1_intercept;
    return { "x":x, "y":y };
  }
}
